import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { BaseChartDirective } from 'ng2-charts';
import { differenceInCalendarDays, format } from 'date-fns';
import type { ChartConfiguration } from 'chart.js';

import { OrdersService, countPrintedOrders } from '../../orders/orders.service';
import { LocationsService } from '../../locations/locations.service';
import { LocationAvatarComponent } from '../statistics-filters/location-select/location-avatar.component';
import { SizeGlyphComponent } from '../statistics-filters/size-select/size-glyph.component';
import { PhotoPricesService } from '../../photo-prices/photo-prices.service';
import { compareSizesByArea, formatSize, getSizeKey } from '../../orders/size.utils';
import type { CountGranularity, OrderCountBucket, OrderCounts } from '../../orders/order.model';
import { StatisticsFiltersComponent, formatDateRange } from '../statistics-filters/statistics-filters.component';
import {
  PLATFORM_OPTIONS,
  defaultStatisticsFilters,
  presetLabel,
  type StatisticsFilters,
} from '../statistics-filters/statistics-filters.model';
import { METRICS, formatMetricValue, getMetric, type StatisticsMetric } from './statistics-metrics';

const BAR_COLOR = '#0A84FF';
const BAR_HOVER_COLOR = '#0060DF';
const FONT_FAMILY = "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif";

const GRANULARITY_FORMAT = {
  day: {
    peakLabel: 'Best day',
    axis: (b: OrderCountBucket) => format(b.start, 'd MMM'),
    tooltip: (b: OrderCountBucket) => format(b.start, 'EEE, d MMM yyyy'),
  },
  week: {
    peakLabel: 'Best week',
    axis: (b: OrderCountBucket) => format(b.start, 'd MMM'),
    tooltip: (b: OrderCountBucket) => formatDateRange(b.start, b.end),
  },
  month: {
    peakLabel: 'Best month',
    axis: (b: OrderCountBucket) => format(b.start, 'MMM yy'),
    tooltip: (b: OrderCountBucket) => format(b.start, 'MMMM yyyy'),
  },
} as const satisfies Record<CountGranularity, {
  peakLabel: string;
  axis: (bucket: OrderCountBucket) => string;
  tooltip: (bucket: OrderCountBucket) => string;
}>;

@Component({
  selector: 'app-statistics',
  imports: [BaseChartDirective, StatisticsFiltersComponent, LocationAvatarComponent, SizeGlyphComponent],
  templateUrl: './statistics.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatisticsComponent {
  private readonly ordersService = inject(OrdersService);
  private readonly locationsService = inject(LocationsService);
  private readonly photoPricesService = inject(PhotoPricesService);

  protected readonly filters = signal<StatisticsFilters>(defaultStatisticsFilters());

  // Live company locations, so the kept-alive page picks up new and edited locations.
  protected readonly locations = rxResource({ stream: () => this.locationsService.locations$ });
  protected readonly locationList = computed(() => (this.locations.hasValue() ? this.locations.value() : []));

  // Sizes of the current price list (latest `lastUpdated`), smallest area first.
  private readonly photoPrice = rxResource({ stream: () => this.photoPricesService.getLatestPhotoPrice() });
  protected readonly sizes = computed(() => {
    const sizes = this.photoPrice.hasValue() ? this.photoPrice.value()?.sizes ?? [] : [];
    return [...sizes].sort(compareSizesByArea);
  });

  protected readonly selectedSize = computed(() => {
    const { sizeKey } = this.filters();
    const size = sizeKey === 'all' ? undefined : this.sizes().find(s => getSizeKey(s) === sizeKey);
    return size ? { size, label: formatSize(size) } : null;
  });

  protected readonly selectedLocation = computed(() => {
    const { locationId } = this.filters();
    return locationId === 'all' ? null : this.locationList().find(l => l.id === locationId) ?? null;
  });

  // Only the date range goes to Firestore. The equality check stops platform changes
  // (and re-selecting the same range) from creating a new range and triggering a fetch.
  private readonly range = computed(
    () => ({ from: this.filters().from, to: this.filters().to }),
    { equal: (a, b) => a.from.getTime() === b.from.getTime() && a.to.getTime() === b.to.getTime() }
  );

  protected readonly orders = rxResource({
    params: () => this.range(),
    stream: ({ params }) => this.ordersService.getPrintedOrders(params.from, params.to),
  });

  // Platform filtering and bucketing run in the browser on the fetched orders.
  private readonly counts = computed(() =>
    this.orders.hasValue() ? countPrintedOrders(this.orders.value(), this.filters()) : undefined
  );

  // Keeps the previous result on screen while a new date range is loading.
  protected readonly data = linkedSignal<OrderCounts | undefined, OrderCounts | undefined>({
    source: this.counts,
    computation: (value, previous) => value ?? previous?.value,
  });

  private readonly buckets = computed(() => this.data()?.buckets ?? []);
  private readonly format = computed(() => GRANULARITY_FORMAT[this.data()?.granularity ?? 'day']);

  protected readonly metrics = METRICS;
  protected readonly selectedMetric = signal<StatisticsMetric>('count');
  private readonly metric = computed(() => getMetric(this.selectedMetric()));

  protected readonly totals = computed(() =>
    this.buckets().reduce(
      (sum, b) => ({ count: sum.count + b.count, revenue: sum.revenue + b.revenue, photos: sum.photos + b.photos }),
      { count: 0, revenue: 0, photos: 0 } satisfies Record<StatisticsMetric, number>
    )
  );

  protected readonly formattedTotals = computed(() => {
    const totals = this.totals();
    return METRICS.map(({ key, label }) => ({ key, label, value: formatMetricValue(key, totals[key]) }));
  });

  protected readonly average = computed(() => {
    const { from, to } = this.filters();
    const days = differenceInCalendarDays(to, from) + 1;
    const value = this.totals()[this.selectedMetric()] / days;
    return this.selectedMetric() === 'revenue'
      ? formatMetricValue('revenue', value)
      : (Math.round(value * 10) / 10).toString();
  });

  protected readonly peak = computed(() => {
    const key = this.selectedMetric();
    const best = this.buckets().reduce<OrderCountBucket | undefined>(
      (best, b) => (b[key] > 0 && (!best || b[key] > best[key]) ? b : best),
      undefined
    );
    return best && { value: formatMetricValue(key, best[key]), label: this.format().axis(best) };
  });

  protected readonly peakLabel = computed(() => this.format().peakLabel);

  protected readonly rangeLabel = computed(() => {
    const { preset, from, to } = this.filters();
    return `${presetLabel(preset)} · ${formatDateRange(from, to)}`;
  });

  protected readonly platformLabel = computed(() => {
    const { platform } = this.filters();
    return platform === 'all' ? null : PLATFORM_OPTIONS.find(o => o.value === platform)!;
  });

  protected readonly chartData = computed<ChartConfiguration<'bar'>['data']>(() => {
    const { axis } = this.format();
    const key = this.selectedMetric();
    const buckets = this.buckets();
    return {
      labels: buckets.map(axis),
      datasets: [{
        // Rounded to cents so summed float prices don't show as 84.49999999.
        data: buckets.map(b => Math.round(b[key] * 100) / 100),
        backgroundColor: BAR_COLOR,
        hoverBackgroundColor: BAR_HOVER_COLOR,
        borderRadius: buckets.length > 60 ? 3 : 6,
        borderSkipped: false,
        barPercentage: 0.7,
        categoryPercentage: 0.9,
      }],
    };
  });

  protected readonly chartOptions = computed<ChartConfiguration<'bar'>['options']>(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 400 },
    interaction: { mode: 'index', intersect: false },
    font: { family: FONT_FAMILY },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: '#8E8E93',
          font: { family: FONT_FAMILY, size: 11 },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 6,
        },
      },
      y: {
        position: 'right',
        beginAtZero: true,
        grid: { color: '#E5E7EB' },
        border: { display: false, dash: [4, 4] },
        ticks: {
          color: '#8E8E93',
          font: { family: FONT_FAMILY, size: 11 },
          precision: this.selectedMetric() === 'revenue' ? undefined : 0,
          maxTicksLimit: 5,
          callback: value => this.metric().formatAxis(Number(value)),
          padding: 8,
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#FFFFFF',
        borderColor: '#E5E7EB',
        borderWidth: 1,
        titleColor: '#6B7280',
        bodyColor: '#111827',
        titleFont: { family: FONT_FAMILY, size: 12, weight: 'normal' },
        bodyFont: { family: FONT_FAMILY, size: 14, weight: 'bold' },
        footerColor: '#6B7280',
        footerFont: { family: FONT_FAMILY, size: 12, weight: 'normal' },
        footerMarginTop: 4,
        padding: 10,
        cornerRadius: 10,
        displayColors: false,
        caretSize: 0,
        callbacks: {
          title: items => {
            const bucket = this.buckets()[items[0].dataIndex];
            return bucket ? this.format().tooltip(bucket) : '';
          },
          // Selected metric first (bold body), the other two below in the muted footer.
          label: item => {
            const bucket = this.buckets()[item.dataIndex];
            return bucket ? this.metric().format(bucket[this.selectedMetric()]) : '';
          },
          footer: items => {
            const bucket = this.buckets()[items[0].dataIndex];
            if (!bucket) return [];
            return METRICS
              .filter(m => m.key !== this.selectedMetric())
              .map(m => m.format(bucket[m.key]));
          },
        },
      },
    },
  }));
}
